// @/components/reusable/search-input.jsx
// V2 Search Input - Reusable search input component matching DropdownSelect styles

import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';

/**
 * SearchInput - V2 reusable search input with clear button
 *
 * Features:
 * - Matches DropdownSelect ring/border styles exactly
 * - Clear button appears when there's a value
 * - Consistent with v2 design system
 *
 * @param {string} value - Current input value
 * @param {Function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional classes for the container
 * @param {string} inputClassName - Additional classes for the input
 */
const SearchInput = ({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  inputClassName,
}) => (
  <div className={clsx('relative', className)}>
    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={clsx(
        // Base layout - increased right padding for clear button
        'w-52 pl-9 pr-9 py-1.5 text-sm/5 rounded-xl bg-white font-medium text-zinc-900',
        // Ring/border matching DropdownSelect exactly (always inset)
        'ring-1 ring-inset ring-zinc-300 shadow-xs',
        // Placeholder
        'placeholder:text-zinc-400 placeholder:font-normal',
        // Hover states matching DropdownSelect
        'hover:bg-zinc-50 hover:ring-zinc-400',
        // Focus states - keep ring-inset so border stays inside
        'focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary',
        // Transition
        'transition-all duration-150',
        inputClassName
      )}
    />
    {/* Clear button - only shown when there's a value */}
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors duration-150"
        aria-label="Clear search"
      >
        <XMarkIcon className="size-4" />
      </button>
    )}
  </div>
);

export { SearchInput };
export default SearchInput;
